import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { ChevronDown } from "lucide-react";
import {
  forwardRef,
  type CSSProperties,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import {
  canvasToolbarButtonVariants,
  canvasToolbarTileVariants,
} from "./canvasToolbarStyles";

interface CanvasToolbarButtonProps extends ComponentPropsWithoutRef<"button"> {
  /** Tooltip label; omit to hide tooltip. */
  label?: ReactNode;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  active?: boolean;
  destructive?: boolean;
  accent?: boolean;
  dropdown?: boolean;
  /** Optional mono text label inside the button (e.g. zoom %). */
  monoLabel?: ReactNode;
  /** `tile` is the commanding bar's 40px square; `sm` the 32px control. */
  size?: "sm" | "tile";
  /**
   * The colour a selected tile is about to place, as a CSS colour; drives
   * the selection wash and glyph through `--xtb-sel-ink`. Null or absent
   * leaves the register on rust.
   */
  ink?: string | null;
  /** The pick's name — "Butter" — appended to the label and accessible name. */
  pick?: string | null;
}

export const CanvasToolbarButton = forwardRef<
  HTMLButtonElement,
  CanvasToolbarButtonProps
>(function CanvasToolbarButton(
  {
    label,
    tooltipSide = "top",
    active = false,
    destructive = false,
    accent = false,
    dropdown = false,
    monoLabel,
    size = "sm",
    ink,
    pick,
    className,
    children,
    disabled,
    style,
    ...rest
  },
  ref,
) {
  const tile = size === "tile";
  const named =
    typeof label === "string" && pick ? `${label} · ${pick}` : label;
  const inkStyle = ink
    ? ({ ...style, "--xtb-sel-ink": ink } as CSSProperties)
    : style;
  const button = (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      aria-pressed={active ? true : undefined}
      // String tooltip labels double as the accessible name (design-system
      // toolbar contract); an explicit aria-label in `rest` still wins.
      aria-label={typeof named === "string" ? named : undefined}
      className={cn(
        tile
          ? canvasToolbarTileVariants()
          : canvasToolbarButtonVariants({
              active,
              destructive,
              accent,
              hasDropdown: dropdown,
            }),
        className,
      )}
      style={inkStyle}
      {...rest}
    >
      <span className="inline-flex shrink-0 items-center justify-center">
        {children}
      </span>
      {monoLabel != null && (
        <span className="font-mono text-xs leading-none">{monoLabel}</span>
      )}
      {dropdown && !tile && (
        <span className="inline-flex shrink-0 items-center text-n-50 group-hover:text-n-70">
          <ChevronDown className="size-2.5" strokeWidth={2.5} />
        </span>
      )}
    </button>
  );

  if (!named) {
    return button;
  }

  return (
    // 400ms show delay — the design-system toolbar tooltip cadence. A tile's
    // tooltip clears the 48px-blur shadow a 46px bar casts, so it stands 16
    // off; the 32px controls keep the kit's 12.
    <Tooltip delayDuration={400}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side={tooltipSide}
        sideOffset={tile ? 16 : 12}
        // A tile's tooltip stands over its docked tray; it must not take the
        // pointer from it.
        className={tile ? "pointer-events-none" : undefined}
      >
        {named}
      </TooltipContent>
    </Tooltip>
  );
});
